import { Button, Divider, Group, Stack } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { ReactNode } from "react";
import { ipawsAlertFormStateSchemaToMessageDataInput, useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { useIpawsAlertFormAllowedEventCodes, useIpawsAlertFormAllowedGeoCodes } from "shared/components/ipaws/alert/query";
import { useIpawsAlertFormActions } from "shared/components/ipaws/alert/useIpawsAlertFormActions";
import { useCustomer } from "shared/graphql/cust";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useFormState } from "shared/hooks/useFormState";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

export const IpawsTemplateFormStepLayout = (props: { children: ReactNode; draftId: string }) => {
    const form = useIpawsAlertFormContext();
    const actions = useIpawsAlertFormActions();

    const { data: customer } = useCustomer();
    const { isFetching: isAllowedGeoCodesFetching } = useIpawsAlertFormAllowedGeoCodes();
    const { isFetching: isAllowedEventCodesFetching } = useIpawsAlertFormAllowedEventCodes();
    const createTemplate = useGraphqlMutation(muts.createTemplate);

    const [step] = useFormState(form, "meta.step");
    const [mode] = useFormState(form, "meta.mode");
    const [channels] = useFormState(form, "channels");

    const nav = useNavigate();
    const blocker = useDirtyFormBlocker(form);

    const isNew = mode == "new";

    const isConfigLoading = step == 0 && channels.length > 0 && (isAllowedGeoCodesFetching || isAllowedEventCodesFetching);

    return (
        <Stack>
            {props.children}
            <Divider />
            <Group>
                {step > 0 ? (
                    <Button variant="default" leftSection={<IconChevronLeft size={16} />} onClick={actions.prevStep}>
                        Back
                    </Button>
                ) : (
                    <div />
                )}
                {step < 3 ? (
                    <Button
                        variant="default"
                        rightSection={<IconChevronRight size={16} />}
                        onClick={actions.nextStep}
                        loading={isConfigLoading}
                    >
                        Next
                    </Button>
                ) : (
                    <Group>
                        <Button
                            onClick={() =>
                                blocker.bypass(async () => {
                                    if (form.validate().hasErrors) {
                                        logger.warn("Validation failed with: ", form.errors);
                                        notify.show.warn({ message: "Please fix issues before continuing" });
                                        return;
                                    }

                                    const msg = ipawsAlertFormStateSchemaToMessageDataInput.parse({
                                        ...form.getValues(),
                                        customerId: customer.id,
                                    });

                                    const { close } = await notify.confirm("Are you sure you want to save this template?");

                                    if (!isNew) {
                                        await actions.updateDraft.mutateAsync({
                                            id: props.draftId,
                                            msg,
                                        });
                                        await queryClient.invalidateQueries({ queryKey: ["ipaws", "draft", props.draftId] });
                                    } else {
                                        await actions.createDraft
                                            .mutateAsync({
                                                msg,
                                            })
                                            .then((res) => createTemplate.mutateAsync({ id: res.draft.id }));
                                    }

                                    await queryClient.invalidateQueries({ queryKey: ["ipaws", "templates"] });
                                    await nav({ to: "/ipaws/templates" });

                                    close();
                                })
                            }
                        >
                            {isNew ? "Save as Template" : "Save Template"}
                        </Button>
                    </Group>
                )}
            </Group>
        </Stack>
    );
};

const muts = {
    createTemplate: graphql(`
        mutation CreateIpawsTemplate($id: UUID!) {
            createIpawsTemplate(alertId: $id) {
                id
            }
        }
    `),
};
